import { useEffect, useState } from "react";

import styles from "./ReportProblemButton.module.css";

const REPORT_EMAIL = "giulio.mattolin@ch.abb.com";

interface Props {
    username?: string;
}

export const ReportProblemButton = ({ username }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState("");

    const close = () => {
        setIsOpen(false);
        setMessage("");
    };

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    const handleSend = () => {
        const subject = "[Ask Watty] Problem report";
        const details = [
            username && `User: ${username}`,
            `Page: ${window.location.href}`,
            `Date: ${new Date().toISOString()}`,
            `Browser: ${navigator.userAgent}`
        ].filter(Boolean);
        const body = [message.trim(), "", "---", ...details].join("\n");

        // Outlook on the web avoids depending on the OS mailto: handler, which is often not Outlook
        const params = new URLSearchParams({ to: REPORT_EMAIL, subject, body });
        window.open(`https://outlook.office.com/mail/deeplink/compose?${params.toString().replace(/\+/g, "%20")}`, "_blank", "noopener,noreferrer");
        close();
    };

    return (
        <>
            <button className={styles.reportButton} onClick={() => setIsOpen(true)} aria-label="Report a problem" title="Report a problem">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-4 4V5a1 1 0 0 1 1-1z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                    />
                    <path d="M12 8v4M12 14.5v.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <span className={styles.reportButtonLabel}>Report a problem</span>
            </button>

            {isOpen && (
                <div className={styles.overlay} onMouseDown={close}>
                    <div
                        className={styles.dialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="report-problem-dialog-title"
                        onMouseDown={e => e.stopPropagation()}
                    >
                        <h2 id="report-problem-dialog-title" className={styles.title}>
                            Report a problem
                        </h2>

                        <label className={styles.label} htmlFor="report-problem-message">
                            Message
                        </label>
                        <textarea
                            id="report-problem-message"
                            className={styles.textarea}
                            rows={6}
                            value={message}
                            placeholder={"Describe the technical problem (e.g. error message, app not loading, broken document links, feature not working, ...)\n\nFor incorrect answers, please use thumbs up/down below the answer."}
                            onChange={e => setMessage(e.target.value)}
                            autoFocus
                        />

                        <div className={styles.actions}>
                            <button className={styles.secondaryButton} onClick={close}>
                                Cancel
                            </button>
                            <button className={styles.primaryButton} onClick={handleSend} disabled={!message.trim()}>
                                Send via email
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
